<template>
  <RevAccordionItem @click="handleClick">
    <template #prefix>
      <IconAttachment />
    </template>
    <template #title>
      <div class="flex items-center gap-8">
        {{ i18n(translations.title) }}
        <ClientOnly>
          <RevTag
            v-if="shouldDisplayNotification"
            :label="i18n(translations.notification)"
            size="small"
            variant="info"
          />
        </ClientOnly>
      </div>
    </template>
    <template #description>
      <span data-test="description">
        {{
          i18n(translations.description, {
            count: attachments.length,
          })
        }}
      </span>
    </template>
    <template #body>
      <p v-if="attachments.length === 0">
        {{ i18n(translations.noAttachmentText) }}
      </p>
      <ol class="divide-static-default-low space-y-24 divide-y">
        <li
          v-for="date in Object.keys(attachmentsByDate)"
          :key="date"
          class="pt-24 first:pt-0"
          data-test="attachments-for-one-day"
        >
          <p class="text-static-default-low body-2 mb-12">
            {{ i18n.date(new Date(parseInt(date, 10))) }}
          </p>

          <ol class="grid grid-cols-1 gap-12 lg:grid-cols-2">
            <li
              v-for="attachment in attachmentsByDate[date]"
              :key="attachment.attachmentId"
            >
              <RevDocument
                v-if="attachment.name && attachment.url"
                class="w-full"
                :name="attachment.name"
                :url="attachment.url"
              />
            </li>
          </ol>
        </li>
      </ol>
    </template>
  </RevAccordionItem>
</template>

<script setup lang="ts">
import { computed } from 'vue'

import type { DateTime } from '@backmarket/http-api'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useLocalStorage } from '@backmarket/utils/composables/useLocalStorage'
import { RevAccordionItem } from '@ds/components/AccordionItem'
import { RevDocument } from '@ds/components/Document'
import { RevTag } from '@ds/components/Tag'
import { IconAttachment } from '@ds/icons/IconAttachment'

import type { CareFolder } from '~/api/care-folders/types/care-folder'

import { LOCAL_STORAGE_KEY_PREFIX } from './Attachments.constants'
import translations from './Attachments.translations'

type Attachment = CareFolder['attachments'][number]

const i18n = useI18n()

const { attachments, customerRequestId, lastAttachmentSharedAt } = defineProps<{
  attachments: Attachment[]
  customerRequestId: CareFolder['customerRequestId']
  lastAttachmentSharedAt: DateTime | null
}>()

const attachmentsByDate = computed(() => {
  const uploadedAttachments = attachments.filter(
    (attachment) => attachment.name && attachment.url,
  )

  return uploadedAttachments.reduce<Record<string, Attachment[]>>(
    (acc, attachment) => {
      const startOfDay = new Date(attachment.creationDate)
        .setHours(0, 0, 0, 0)
        .toString()

      const existingAttachments = acc[startOfDay] || []

      return { ...acc, [startOfDay]: [...existingAttachments, attachment] }
    },
    {},
  )
})

const localStorage = useLocalStorage()

const logToStorage = () => {
  const now = new Date().getTime().toString()
  localStorage.setItem(`${LOCAL_STORAGE_KEY_PREFIX}${customerRequestId}`, now)
}

const handleClick = () => {
  logToStorage()
}

const shouldDisplayNotification = computed(() => {
  const lastClick = localStorage.getItem(
    `${LOCAL_STORAGE_KEY_PREFIX}${customerRequestId}`,
  )

  if (!lastClick && lastAttachmentSharedAt) {
    // when the user has never clicked on the accordion, but there are shared attachments
    return true
  }

  if (lastClick && lastAttachmentSharedAt) {
    const lastClickDate = new Date(parseInt(lastClick, 10))
    const lastAttachmentSharedAtDate = new Date(lastAttachmentSharedAt)

    // true if the last shared attachment is more recent than the last click
    return lastAttachmentSharedAtDate.getTime() > lastClickDate.getTime()
  }

  return false
})
</script>
